import { useRef, useEffect, useImperativeHandle, forwardRef, useState } from "react";
import { Eraser } from "lucide-react";

const SignaturePad = forwardRef(({ onChange, placeholder = "Draw your signature here", clearLabel = "Clear Signature" }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  const setup = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#D4AF37";
  };

  useEffect(() => {
    setup();
    const handler = () => setup();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
    // eslint-disable-next-line
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasDrawn.current) {
      hasDrawn.current = true;
      setEmpty(false);
    }
    onChange && onChange(false);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    setEmpty(true);
    onChange && onChange(true);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !hasDrawn.current,
    getDataURL: () => (hasDrawn.current ? canvasRef.current.toDataURL("image/png") : ""),
    clear,
  }));

  return (
    <div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="bg-[#0A0A0A] border border-[#262626] rounded-xl w-full h-48 cursor-crosshair touch-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {empty && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#555] font-sans text-sm">
            {placeholder}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        data-testid="clear-signature-btn"
        className="mt-3 inline-flex items-center gap-2 text-xs font-sans uppercase tracking-widest text-[#A3A3A3] hover:text-white transition-colors"
      >
        <Eraser className="w-3.5 h-3.5" /> {clearLabel}
      </button>
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
